import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Dropdown, Space } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import webrtc from 'services/webrtc';
import { audioCallLocalStream } from 'services/voip';

import MeetingAction from '../MeetingAction';
import { getDevices } from '../_utils';

const DEBOUNCE_TIME = 300;

interface IProps {
  isVideoEnabled: boolean;
  isAudioEnabled: boolean;
}

const AudioRouteButton: FC<IProps> = ({ isVideoEnabled, isAudioEnabled }) => {
  const timer = useRef<NodeJS.Timeout | null>(null);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedId, setSelectedId] = useState<string | undefined>();

  const checkSelectedDevice = useCallback(() => {
    const audioTrack = audioCallLocalStream.current?.getAudioTracks()[0];
    const activeDeviceId = audioTrack?.getSettings().deviceId;

    setSelectedId(activeDeviceId);
  }, []);

  const getMicrophoneDevices = useCallback(() => {
    (async () => {
      try {
        const data = await getDevices();

        if (!data?.microphones) {
          throw new Error();
        }

        checkSelectedDevice();
        setDevices(data.microphones);
      } catch {
        setDevices([]);
      }
    })();
  }, [checkSelectedDevice]);

  useEffect(() => {
    getMicrophoneDevices();

    navigator.mediaDevices.addEventListener(
      'devicechange',
      getMicrophoneDevices
    );

    return () => {
      navigator.mediaDevices.removeEventListener(
        'devicechange',
        getMicrophoneDevices
      );
    };
  }, [getMicrophoneDevices]);

  const onSwitchMicrophone = async (device: MediaDeviceInfo) => {
    if (timer.current) {
      clearTimeout(timer.current);
    }

    timer.current = setTimeout(async () => {
      const stream = await webrtc.getUserMedia({
        isVideoEnabled,
        isAudioEnabled,
        audioDeviceId: device.deviceId
      });

      if (stream && audioCallLocalStream.current) {
        const oldTrack = audioCallLocalStream.current.getAudioTracks()?.[0];
        const newTrack = stream?.getAudioTracks()?.[0];

        if (oldTrack) {
          audioCallLocalStream.current.removeTrack(oldTrack);
        }

        audioCallLocalStream.current.addTrack(newTrack);

        webrtc.addOrReplaceTrackToPcs(
          audioCallLocalStream.current,
          'audio',
          oldTrack
        );
        checkSelectedDevice();
      }
    }, DEBOUNCE_TIME);
  };

  return (
    <Dropdown
      menu={{
        items: devices.map(item => ({
          key: item.deviceId,
          label: (
            <Space align="center" size={6}>
              {selectedId === item.deviceId ? (
                <CheckOutlined style={{ fontSize: 12, color: '#18212C' }} />
              ) : (
                <div style={{ width: 12 }} />
              )}
              {item.label}
            </Space>
          ),
          onClick() {
            onSwitchMicrophone(item);
          }
        }))
      }}
      placement="topRight"
      trigger={['click']}
    >
      <MeetingAction icon="speaker" />
    </Dropdown>
  );
};

export default AudioRouteButton;
